import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService from '../services/ApiService';
import ColegioService from '../services/ColegioService';
import ParticularService from '../services/ParticularService';
import { canCreateAssistant, normalizeAssistantPermissions } from '../utils/userPermissions';

// Crear el contexto de autenticación
const AuthContext = createContext();

// Hook personalizado para usar el contexto de autenticación
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

// Funciones para manejar cookies
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  const result = parts.length === 2 ? parts.pop().split(';').shift() : null;
  
  if (result) {
  }
  
  return result;
};

const setCookie = (name, value, days = 7) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
  const cookieString = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Strict`;
  document.cookie = cookieString;
  
};

const deleteCookie = (name) => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
};

const getDecodedCookie = (name) => {
  const value = getCookie(name);
  if (!value) return null;
  try {
    return decodeURIComponent(value);
  } catch (error) {
    return value;
  }
};

const applyRoleMetadata = (userData = {}) => {
  const normalizedUser = { ...userData };
  const cookieRoleId = getDecodedCookie('user_role_id');
  const cookieRoleName = getDecodedCookie('user_role_name');

  if (!normalizedUser.roleId && cookieRoleId) {
    normalizedUser.roleId = cookieRoleId;
  }
  if (!normalizedUser.roleName && cookieRoleName) {
    normalizedUser.roleName = cookieRoleName;
  }
  if (!normalizedUser.role && normalizedUser.roleName) {
    normalizedUser.role = normalizedUser.roleName;
  }

  return normalizeAssistantPermissions(normalizedUser);
};

// Provider del contexto de autenticación
export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Verificar autenticación al cargar la aplicación
  useEffect(() => {
    const checkAuthStatus = () => {
      try {
        
        const authToken = getCookie('auth_token');
        const userData = getCookie('user_data');
        
        if (authToken && userData) {
          try {
            const parsedUser = JSON.parse(decodeURIComponent(userData));
            const normalizedUser = applyRoleMetadata(parsedUser);
            setUser(normalizedUser);
            setIsAuthenticated(true);
            
            // ¡CRÍTICO! Configurar el token en ApiService al restaurar la sesión
            apiService.setAuthToken(authToken);
            
          } catch (error) {
            console.error('❌ Error al parsear datos del usuario:', error);
            logout();
          }
        } else {
          setIsAuthenticated(false);
          setUser(null);
        }
      } catch (error) {
        console.error('❌ Error al verificar autenticación:', error);
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  const checkAuthStatus = () => {
    // Función movida dentro del useEffect para evitar dependencias
    // Se mantiene aquí para poder ser llamada externamente si es necesario
    const authToken = getCookie('auth_token');
    const userData = getCookie('user_data');
    
    if (authToken && userData) {
      try {
        const parsedUser = JSON.parse(decodeURIComponent(userData));
        const normalizedUser = applyRoleMetadata(parsedUser);
        setUser(normalizedUser);
        setIsAuthenticated(true);
        
        // También configurar el token en ApiService al recargar
        apiService.setAuthToken(authToken);
        
      } catch (error) {
        console.error('❌ Error al recargar autenticación:', error);
        logout();
      }
    } else {
      logout();
    }
  };

  const login = async (userData) => {
    try {
      setIsLoading(true);

      // Los datos ya vienen validados del UsuarioService, solo guardamos
      const roleId = userData.idRol || userData.roleId || null;
      const roleName = userData.nombreRol || userData.roleName || userData.role || 'Usuario';

      const areaName = userData.area || userData.Area || userData.areaNombre || userData.nombreArea || '';
      const assistantPermission = canCreateAssistant(userData);

      const userDataToStore = {
        id: userData.email, // Usar email como ID único
        email: userData.email,
        name: userData.name,
        displayName: userData.name,
        pictureUrl: userData.pictureUrl || '',
        token: userData.token,
        permiteCrearAsistente: assistantPermission,
        pertmiteCrearAsistente: assistantPermission,
        permiteCrearAula: userData.permiteCrearAula || false,
        roleId,
        roleName,
        role: roleName,
        particulares: userData.particulares || userData.Particulares || [],
        lastLogin: new Date().toISOString(),
        area: areaName,
        statusCode: userData.statusCode
      };

      // Guardar en cookies
      setCookie('auth_token', userData.token, 7); // 7 días
      setCookie('user_data', encodeURIComponent(JSON.stringify(userDataToStore)), 7);
      if (roleId) {
        setCookie('user_role_id', encodeURIComponent(roleId), 7);
      }
      if (roleName) {
        setCookie('user_role_name', encodeURIComponent(roleName), 7);
      }

      setUser(userDataToStore);
      setIsAuthenticated(true);

      
      return { success: true, user: userDataToStore };
    } catch (error) {
      console.error('❌ Error procesando login:', error);
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    
    // Eliminar cookies
    deleteCookie('auth_token');
    deleteCookie('user_data');
    deleteCookie('user_role_id');
    deleteCookie('user_role_name');

    // Limpiar datos de colegios
    ColegioService.clearColegioData();
    ParticularService.clearParticularData();
    
    // Limpiar token del ApiService
    apiService.setAuthToken(null);
    
    // Limpiar estado
    setUser(null);
    setIsAuthenticated(false);
    
  };

  const updateUser = (newUserData) => {
    const updatedUser = { ...user, ...newUserData };
    setUser(updatedUser);
    setCookie('user_data', encodeURIComponent(JSON.stringify(updatedUser)), 7);
    if (updatedUser.roleId) {
      setCookie('user_role_id', encodeURIComponent(updatedUser.roleId), 7);
    }
    if (updatedUser.roleName) {
      setCookie('user_role_name', encodeURIComponent(updatedUser.roleName), 7);
    }
  };

  const updateToken = (token) => {
    if (!token) return;
    setCookie('auth_token', token, 7);
    apiService.setAuthToken(token);
    updateUser({ token });
  };

  // Función para manejar errores 401 y logout automático
  const handleUnauthorized = () => {
    console.warn('🔐 Error 401 detectado - Cerrando sesión automáticamente');
    
    // Ejecutar logout inmediatamente
    logout();
    
    // No forzar redirección aquí, dejar que ProtectedRoute maneje la lógica
  };

  // Funciones auxiliares eliminadas - ya no son necesarias

  const contextValue = {
    isAuthenticated,
    user,
    isLoading,
    login,
    logout,
    updateUser,
    updateToken,
    checkAuthStatus,
    handleUnauthorized
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
